import { Box, IconButton, InputAdornment, Link as MuiLink, Stack, TextField, Typography } from '@mui/material'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import LoadingButton from '@mui/lab/LoadingButton'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import authApi from '../api/authApi'
import { fieldErrors, errorMessage } from '../utils/errors'
import { setToken } from '../utils/storage'
import { useToast } from '../components/common/ToastProvider'

const Login = () => {
  const navigate = useNavigate()
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState({})

  const handleSubmit = async (event) => {
    event.preventDefault()
    setErrors({})

    const data = new FormData(event.target)
    const username = String(data.get('username') || '').trim()
    const password = String(data.get('password') || '').trim()

    const nextErrors = {}
    if (!username) nextErrors.username = 'Enter your username'
    if (!password) nextErrors.password = 'Enter your password'
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return
    }

    setLoading(true)
    try {
      const res = await authApi.login({ username, password })
      setToken(res.token)
      navigate('/', { replace: true })
    } catch (err) {
      const fields = fieldErrors(err)
      setErrors(fields)
      if (!fields.username && !fields.password) {
        toast.error(errorMessage(err, 'Could not log you in.'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box>
      <Typography variant='h2' sx={{ mb: 1 }}>
        Welcome back
      </Typography>
      <Typography color='text.secondary' sx={{ mb: 4 }}>
        Log in to pick up where your boards left off.
      </Typography>

      <Box component='form' onSubmit={handleSubmit} noValidate>
        <Stack spacing={2.5}>
          <TextField
            id='username'
            name='username'
            label='Username'
            autoComplete='username'
            autoFocus
            fullWidth
            disabled={loading}
            error={Boolean(errors.username)}
            helperText={errors.username}
          />
          <TextField
            id='password'
            name='password'
            label='Password'
            type={showPassword ? 'text' : 'password'}
            autoComplete='current-password'
            fullWidth
            disabled={loading}
            error={Boolean(errors.password)}
            helperText={errors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    edge='end'
                    size='small'
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((visible) => !visible)}
                  >
                    {showPassword ? <VisibilityOffOutlinedIcon fontSize='small' /> : <VisibilityOutlinedIcon fontSize='small' />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <LoadingButton
            type='submit'
            variant='contained'
            size='large'
            fullWidth
            loading={loading}
            sx={{ mt: 0.5 }}
          >
            Log in
          </LoadingButton>
        </Stack>
      </Box>

      <Typography variant='body2' color='text.secondary' sx={{ mt: 3 }}>
        No account yet?{' '}
        <MuiLink component={Link} to='/signup' underline='hover' color='primary.main' fontWeight={600}>
          Create one
        </MuiLink>
      </Typography>
    </Box>
  )
}

export default Login
